import React, { useState, useEffect, useRef } from 'react';
import { Globe, Upload, Trash2, Eye, ChevronDown, ChevronUp, AlertCircle, CheckCircle, RefreshCw, Plus, X, FileText } from 'lucide-react';
import { adminService } from '../../../services/adminService';

const BibleLanguageManager = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedLanguage, setExpandedLanguage] = useState(null);
  const [languageMappings, setLanguageMappings] = useState({});
  const [loadingMappings, setLoadingMappings] = useState({});
  const [showImportForm, setShowImportForm] = useState(false);
  const [importLanguageName, setImportLanguageName] = useState('');
  const [importJson, setImportJson] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [deletingLanguage, setDeletingLanguage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadLanguages();
  }, []);

  const loadLanguages = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminService?.listBibleLanguages();
      setLanguages(data || []);
    } catch (err) {
      setError('Failed to load languages: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguageExpand = async (languageName) => {
    if (expandedLanguage === languageName) {
      setExpandedLanguage(null);
      return;
    }
    setExpandedLanguage(languageName);
    if (!languageMappings?.[languageName]) {
      setLoadingMappings(prev => ({ ...prev, [languageName]: true }));
      try {
        const data = await adminService?.getLanguageMappings(languageName);
        setLanguageMappings(prev => ({ ...prev, [languageName]: data }));
      } catch (err) {
        setError('Failed to load mappings: ' + err?.message);
      } finally {
        setLoadingMappings(prev => ({ ...prev, [languageName]: false }));
      }
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setImportJson(evt?.target?.result || '');
    };
    reader?.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (!importLanguageName?.trim()) {
      setError('Please enter a language name');
      return;
    }
    if (!importJson?.trim()) {
      setError('Please provide JSON data or upload a file');
      return;
    }

    setImporting(true);
    setError('');
    setSuccess('');
    setImportResults(null);

    try {
      const results = await adminService?.importLanguageMappings(importLanguageName?.trim(), importJson);
      setImportResults(results);
      if (results?.failed === 0) {
        setSuccess(`Successfully imported ${results?.inserted} new + ${results?.updated} updated mappings for "${importLanguageName}"`);
      } else {
        setSuccess(`Import completed: ${results?.inserted} inserted, ${results?.updated} updated, ${results?.failed} failed`);
      }
      // Refresh language list and clear cached mappings for this language
      setLanguageMappings(prev => {
        const updated = { ...prev };
        delete updated?.[importLanguageName?.toLowerCase()?.trim()];
        return updated;
      });
      await loadLanguages();
      setShowImportForm(false);
      setImportLanguageName('');
      setImportJson('');
    } catch (err) {
      setError('Import failed: ' + err?.message);
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteLanguage = async (languageName) => {
    if (!window.confirm(`Delete all book name mappings for language "${languageName}"? This cannot be undone.`)) return;
    setDeletingLanguage(languageName);
    setError('');
    try {
      const result = await adminService?.deleteLanguageMappings(languageName);
      if (result?.success === false) {
        setError(result?.error || 'Cannot delete this language');
      } else {
        setSuccess(`Deleted ${result?.deleted_count || 0} mappings for "${languageName}"`);
        setLanguageMappings(prev => {
          const updated = { ...prev };
          delete updated?.[languageName];
          return updated;
        });
        if (expandedLanguage === languageName) setExpandedLanguage(null);
        await loadLanguages();
      }
    } catch (err) {
      setError('Delete failed: ' + err?.message);
    } finally {
      setDeletingLanguage(null);
    }
  };

  const sampleJson = `[
  {"id":1,"name":"Genèse","shortname":null,"matching1":null,"matching2":null},
  {"id":2,"name":"Exode","shortname":null,"matching1":null,"matching2":null},
  {"id":3,"name":"Lévitique","shortname":null,"matching1":null,"matching2":null}
]`;

  const coreLanguages = ['english', 'abbreviation', 'long_form', 'alternative_spelling'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Bible Language Book Names
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage multilingual book name mappings so the Bible reader can find verses in any language version.
          </p>
        </div>
        <button
          onClick={() => { setShowImportForm(!showImportForm); setError(''); setSuccess(''); setImportResults(null); }}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Language
        </button>
      </div>
      {/* Alerts */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <span className="text-destructive text-sm">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-destructive/60 hover:text-destructive"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-3 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 text-success shrink-0 mt-0.5" />
          <span className="text-success text-sm">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto text-success/60 hover:text-success"><X className="w-4 h-4" /></button>
        </div>
      )}
      {/* Import Form */}
      {showImportForm && (
        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Import Language Book Names</h3>
            <button onClick={() => setShowImportForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Language Name *</label>
            <input
              type="text"
              value={importLanguageName}
              onChange={e => setImportLanguageName(e?.target?.value)}
              placeholder="e.g. french, spanish, german, portuguese"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Use lowercase, single word (e.g. "french", "spanish")</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">JSON Data *</label>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => fileInputRef?.current?.click()}
                className="inline-flex items-center gap-1.5 text-sm text-primary border border-primary/30 hover:bg-primary/5 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload JSON File
              </button>
              <input ref={fileInputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleFileUpload} />
              <button
                onClick={() => setImportJson(sampleJson)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground border border-border hover:bg-muted/30 px-3 py-1.5 rounded-lg transition-colors"
              >
                <FileText className="w-3.5 h-3.5" />
                Load Sample
              </button>
            </div>
            <textarea
              value={importJson}
              onChange={e => setImportJson(e?.target?.value)}
              rows={8}
              placeholder={sampleJson}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Format: <code className="bg-muted px-1 rounded">{"[{\"id\": 1, \"name\": \"Genèse\", \"shortname\": null, \"matching1\": null, \"matching2\": null}, ...]"}</code>
              <br />
              <strong>id</strong> = book number (1=Genesis … 66=Revelation), <strong>name</strong> = primary book name, <strong>shortname/matching1/matching2</strong> = additional aliases
            </p>
          </div>

          {/* Import Results */}
          {importResults && (
            <div className="bg-muted/30 border border-border rounded-lg p-3 text-sm space-y-1">
              <p className="font-medium text-foreground">Import Results:</p>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="text-success">✓ {importResults?.inserted} inserted</span>
                <span className="text-primary">↻ {importResults?.updated} updated</span>
                <span className="text-warning">⇄ {importResults?.reassigned} reassigned</span>
                {importResults?.failed > 0 && <span className="text-destructive">✗ {importResults?.failed} failed</span>}
              </div>
              {importResults?.errors?.length > 0 && (
                <div className="mt-2 max-h-24 overflow-y-auto">
                  {importResults?.errors?.map((e, i) => (
                    <p key={i} className="text-xs text-destructive">{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowImportForm(false)}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted/30 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={importing}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importing ? 'Importing...' : 'Import Mappings'}
            </button>
          </div>
        </div>
      )}
      {/* Languages List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-medium text-foreground text-sm">Configured Languages</h3>
          <button onClick={loadLanguages} className="text-muted-foreground hover:text-foreground transition-colors" title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading languages...</p>
          </div>
        ) : languages?.length === 0 ? (
          <div className="p-8 text-center">
            <Globe className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No language mappings found</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Add a language to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {languages?.map((lang) => {
              const isCore = coreLanguages?.includes(lang?.language_name?.toLowerCase());
              const isExpanded = expandedLanguage === lang?.language_name;
              const mappings = languageMappings?.[lang?.language_name];
              const isLoadingMappings = loadingMappings?.[lang?.language_name];

              return (
                <div key={lang?.language_name}>
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground text-sm capitalize">{lang?.language_name}</span>
                        {isCore && (
                          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">core</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {lang?.mapping_count?.toLocaleString()} mappings
                        {lang?.sample_names?.length > 0 && (
                          <span className="ml-1 opacity-70">· {lang?.sample_names?.slice(0, 3)?.join(', ')}{lang?.sample_names?.length > 3 ? '…' : ''}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleLanguageExpand(lang?.language_name)}
                        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/40 rounded transition-colors"
                        title="View mappings"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!isCore && (
                        <button
                          onClick={() => handleDeleteLanguage(lang?.language_name)}
                          disabled={deletingLanguage === lang?.language_name}
                          className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors disabled:opacity-50"
                          title="Delete language"
                        >
                          {deletingLanguage === lang?.language_name
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <Trash2 className="w-4 h-4" />
                          }
                        </button>
                      )}
                      <button
                        onClick={() => toggleLanguageExpand(lang?.language_name)}
                        className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {/* Expanded Mappings */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted/10 px-4 py-3">
                      {isLoadingMappings ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Loading mappings...
                        </div>
                      ) : mappings?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-muted-foreground border-b border-border">
                                <th className="text-left py-1.5 pr-3 font-medium w-12">#</th>
                                <th className="text-left py-1.5 pr-3 font-medium">Standard Name</th>
                                <th className="text-left py-1.5 font-medium">Variation Name</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                              {mappings?.map((m) => (
                                <tr key={m?.id} className="hover:bg-muted/20">
                                  <td className="py-1.5 pr-3 text-muted-foreground">{m?.book_number}</td>
                                  <td className="py-1.5 pr-3 text-muted-foreground">{m?.standard_book_name}</td>
                                  <td className="py-1.5 text-foreground font-medium">{m?.variation_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">No mappings found for this language.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Help Box */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm">
        <p className="font-medium text-foreground mb-1">How it works</p>
        <ul className="text-muted-foreground space-y-1 text-xs list-disc list-inside">
          <li>Upload a JSON file with book names in your target language</li>
          <li>The <strong>id</strong> field must match the canonical book number (1=Genesis, 19=Psalms, 66=Revelation)</li>
          <li>The Bible reader will automatically use these mappings to find verses when a non-English version is selected</li>
          <li>You can add multiple aliases per book using <code className="bg-muted px-1 rounded">shortname</code>, <code className="bg-muted px-1 rounded">matching1</code>, and <code className="bg-muted px-1 rounded">matching2</code></li>
        </ul>
      </div>
    </div>
  );
};

export default BibleLanguageManager;
